import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import usePageTitle from '@hooks/usePageTitle';
import { useAppSelector } from '@reduxStore';
import { selectAppFeatures } from '@/Infrastructure/Utilities/appSelectors';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import SummaryTab from './Summary/SummaryTab';
import LoadsTab from './Loads/LoadsTab';
import CommissionsTab from './Commissions/CommissionsTab';
import DeductionsTab from './Deductions/DeductionsTab';
import BillingWeekTab from './BillingWeek/BillingWeekTab';
import YtdTab from './YTD/YtdTab';
import StatementSelector from './StatementSelector';
import ExportSettlementButton from './ExportSettlementButton';

import styles from './Page.module.css';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';

const TABS = ['summary', 'loads', 'commissions', 'deductions', 'billing-week', 'ytd-summary'] as const;

export default function AgentSettlementsPage() {
  const { addPageTitle } = usePageTitle();
  const [searchParams, setSearchParams] = useSearchParams();
  const featureFlags = useAppSelector((state) => selectAppFeatures(state));
  const terminal = useCurrentTerminal();
  const [statementId, setStatementId] = useState<number | null>(null);
  const { data: user } = useFetchMyProfileQuery(undefined);

  const activeTab = Math.max(0, TABS.indexOf((searchParams.get('tab') ?? 'summary') as (typeof TABS)[number]));

  useEffect(() => {
    addPageTitle('Agent Settlements');
  }, [addPageTitle]);

  const hasStatement = statementId !== null;

  function handleTabChange(_event: React.SyntheticEvent, newValue: number) {
    setSearchParams((prev) => {
      prev.set('tab', TABS[newValue]);
      return prev;
    });
  }

  function handleStatementChange(newStatementId: number) {
    if (newStatementId === statementId) return;

    setStatementId(newStatementId);
    setSearchParams(
      (prev) => {
        prev.set('tab', TABS[0]);
        return prev;
      },
      { replace: true }
    );
  }

  /** When this feature is not enabled than the user should not see this page */
  if (!featureFlags?.agentSettlements || !user?.canViewAgtSettlements || terminal.isCompanyStore) {
    return (
      <Navigate
        to='/'
        replace
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles['title-bar']}>
        <StatementSelector
          value={statementId}
          onChange={handleStatementChange}
        />

        <span className={styles.title}>Agent Settlements</span>

        <div className={styles['agent-info']}>
          <span>{`${terminal.agtId} - ${terminal.agtName}`}</span>
          <span>{terminal.agtAddress1}</span>
          <span>{`${terminal.agtCity}, ${terminal.agtState} ${terminal.agtZip}`}</span>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', columnGap: 1 }}>
        <Tabs
          sx={{ flex: 1, minWidth: 0 }}
          value={activeTab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile>
          <Tab
            label='Summary'
            id='settlements-tab-0'
            aria-controls='settlements-tab-panel-0'
            disabled={!hasStatement}
          />
          <Tab
            label='Loads'
            id='settlements-tab-1'
            aria-controls='settlements-tab-panel-1'
            disabled={!hasStatement}
          />
          <Tab
            label='Commissions'
            id='settlements-tab-2'
            aria-controls='settlements-tab-panel-2'
            disabled={!hasStatement}
          />
          <Tab
            label='Deductions'
            id='settlements-tab-3'
            aria-controls='settlements-tab-panel-3'
            disabled={!hasStatement}
          />
          <Tab
            label='Billing Week'
            id='settlements-tab-4'
            aria-controls='settlements-tab-panel-4'
            disabled={!hasStatement}
          />
          <Tab
            label='YTD Summary'
            id='settlements-tab-5'
            aria-controls='settlements-tab-panel-5'
            disabled={!hasStatement}
          />
        </Tabs>
        {hasStatement && <ExportSettlementButton statementId={statementId} />}
      </Box>

      {!hasStatement && (
        <div className={styles['empty-state']}>
          <AssessmentOutlinedIcon className={styles['empty-icon']} />
          <Typography className={styles['empty-title']}>Select a statement to begin</Typography>
          <Typography className={styles['empty-subtitle']}>Choose a settlement statement above to view its report.</Typography>
        </div>
      )}

      {hasStatement && (
        <>
          <div
            id='settlements-tab-panel-0'
            role='tabpanel'
            hidden={activeTab !== 0}
            aria-labelledby='settlements-tab-0'
            className={styles['tab-content']}>
            {activeTab === 0 && (
              <SummaryTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>

          <div
            id='settlements-tab-panel-1'
            role='tabpanel'
            hidden={activeTab !== 1}
            aria-labelledby='settlements-tab-1'
            className={styles['tab-content']}>
            {activeTab === 1 && (
              <LoadsTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>

          <div
            id='settlements-tab-panel-2'
            role='tabpanel'
            hidden={activeTab !== 2}
            aria-labelledby='settlements-tab-2'
            className={styles['tab-content']}>
            {activeTab === 2 && (
              <CommissionsTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>

          <div
            id='settlements-tab-panel-3'
            role='tabpanel'
            hidden={activeTab !== 3}
            aria-labelledby='settlements-tab-3'
            className={styles['tab-content']}>
            {activeTab === 3 && (
              <DeductionsTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>

          <div
            id='settlements-tab-panel-4'
            role='tabpanel'
            hidden={activeTab !== 4}
            aria-labelledby='settlements-tab-4'
            className={styles['tab-content']}>
            {activeTab === 4 && (
              <BillingWeekTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>

          <div
            id='settlements-tab-panel-5'
            role='tabpanel'
            hidden={activeTab !== 5}
            aria-labelledby='settlements-tab-5'
            className={styles['tab-content']}>
            {activeTab === 5 && (
              <YtdTab
                terminalNumber={terminal.number}
                statementId={statementId}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
